// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Link, { LinkPresentationProps } from "@conklin/MobiusOverrides/Link/Link";
import { generateLinksFrom } from "@conklin/Overrides/Components/PageBreadcrumbs";
import { getFormatDateWithSuffix } from "@conklin/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
    // XNG-Changes
    links: state.components.breadcrumbs.links,
    linksState: state.links,
    nodeId: getCurrentPage(state).nodeId,
    homePageUrl: getHomePageUrl(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface NewsPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    // XNG-Changes:
    categoryLink?: LinkPresentationProps;
}

export const newsPageViewStyles: NewsPageViewStyles = {
    newsWrapper: {},
    contentWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
};

const styles = newsPageViewStyles;

// XNG-Changes
const NewsPageView = ({ page, links, linksState, nodeId, homePageUrl, history }: Props) => {
    if (!page) {
        return null;
    }
    // XNG-Changes
    const currentPageUrlPathLinks = page.fields["category"]
        ? links || generateLinksFrom(linksState, nodeId, homePageUrl)
        : [];

    const handleClick = () => {
        if (currentPageUrlPathLinks.length > 1 && page.fields["category"]) {
            const { href: baseUrl } = currentPageUrlPathLinks[currentPageUrlPathLinks.length - 2];
            const newsListPageCategorySelectedHref = `${baseUrl}?page=${1}&pageSize=${12}${`&categories=${page.fields["category"]}`}`;
            return baseUrl && history.replace(newsListPageCategorySelectedHref);
        }
    };
    return (
        <StyledWrapper {...styles.newsWrapper} key={page.id}>
            {/* XNG-Changes: added news page category */}
            {page.fields["category"] && (
                <Link
                    {...styles.categoryLink}
                    data-test-selector="newslist_category"
                    onClick={handleClick}
                    aria-label={page.fields["category"]}
                >
                    {page.fields["category"]}
                </Link>
            )}
            <StyledWrapper {...styles.dateAndAuthorWrapper}>
                {page.fields["author"] && (
                    <Typography {...styles.authorText} data-test-selector="newslist_author">
                        {page.fields["author"]}
                    </Typography>
                )}
                {/* XNG-Changes: changed date format */}
                {page.fields["publishDate"] && (
                    <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                        {/* <LocalizedDateTime
                            dateTime={new Date(page.fields["publishDate"].replace(/\//g, ','))}
                            options={{ year: "numeric", month: "long", day: "numeric" }}
                        /> */}
                        {getFormatDateWithSuffix(new Date(page.fields["publishDate"]))}
                    </Typography>
                )}
            </StyledWrapper>
            {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                <StyledWrapper {...styles.contentWrapper} data-test-selector="newslist_content">
                    {parse(page.fields["newsContent"], parserOptions)}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps)(NewsPageView)),
    definition: {
        group: "News Page",
        allowedContexts: ["NewsPage"],
    },
};

export default widgetModule;
